import { DiagramFrame } from "@/components/diagrams/DiagramFrame";
import {
  aud,
  cashWeeks,
  cooResult,
  gridResult,
  priceMove,
  PRICE_TABLE_MOVES,
  ZONES,
  type CashInputs,
  type CooInputs,
  type GridInputs,
  type LeakInputs,
  type PriceInputs,
  type ScoreInputs,
} from "@/lib/tools/calc";

const short = (n: number) => {
  const a = Math.abs(n);
  const s = a >= 1e6 ? `$${(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `$${Math.round(a / 1e3)}k` : `$${Math.round(a)}`;
  return n < 0 ? `-${s}` : s;
};

function Frame({ title, desc, caption, children, vb = "0 0 720 300" }: { title: string; desc: string; caption?: string; children: React.ReactNode; vb?: string }) {
  return (
    <DiagramFrame scrollable={false} caption={caption} captionClassName="mt-2 text-xs">
      <svg viewBox={vb} role="img" aria-label={`${title}. ${desc}`} className="h-auto w-full max-w-full">
        {children}
      </svg>
    </DiagramFrame>
  );
}

export function CashChart({ inputs }: { inputs: CashInputs }) {
  const r = cashWeeks(inputs);
  const vals = [...r.weeks.map((w) => w.closing), inputs.floor, 0, inputs.cash];
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  const L = 70, R = 700, T = 20, B = 250;
  const y = (v: number) => B - ((v - min) / (max - min || 1)) * (B - T);
  const x = (k: number) => L + (k * (R - L)) / 12;
  const path = r.weeks.map((w, k) => `${k ? "L" : "M"}${x(k).toFixed(1)} ${y(w.closing).toFixed(1)}`).join(" ");
  const lo = r.lowest.week - 1;
  return (
    <Frame title="13 week closing cash" desc={`Closing cash by week with the comfort floor of ${aud(inputs.floor)} as a dashed line. Lowest point ${aud(r.lowest.closing)} in week ${r.lowest.week}.`}>
      {min < 0 ? <line x1={L} x2={R} y1={y(0)} y2={y(0)} className="stroke-charcoal/40" strokeWidth="1" /> : null}
      <line x1={L} x2={R} y1={y(inputs.floor)} y2={y(inputs.floor)} className="stroke-gold" strokeWidth="2" strokeDasharray="6 5" />
      <text x={R} y={y(inputs.floor) - 6} textAnchor="end" fontSize="12" className="fill-charcoal">Comfort floor {short(inputs.floor)}</text>
      <path d={path} fill="none" className="stroke-midnight" strokeWidth="3" strokeLinejoin="round" />
      {r.weeks.map((w, k) => (
        <g key={w.week}>
          <circle cx={x(k)} cy={y(w.closing)} r={k === lo ? 7 : 3.5} className={w.closing < 0 ? "fill-destructive" : k === lo ? "fill-gold" : "fill-midnight"} />
          <text x={x(k)} y={B + 22} textAnchor="middle" fontSize="12" className="fill-charcoal">{w.week}</text>
        </g>
      ))}
      <text x={x(lo)} y={y(r.lowest.closing) + (y(r.lowest.closing) > B - 30 ? -14 : 24)} textAnchor={lo >= 11 ? "end" : lo <= 1 ? "start" : "middle"} fontSize="13" fontWeight="600" className="fill-midnight">
        Lowest {short(r.lowest.closing)}
      </text>
      <text x={L - 8} y={y(max) + 4} textAnchor="end" fontSize="11" className="fill-charcoal">{short(max)}</text>
      <text x={L - 8} y={y(min) + 4} textAnchor="end" fontSize="11" className="fill-charcoal">{short(min)}</text>
      <text x={(L + R) / 2} y={B + 42} textAnchor="middle" fontSize="12" className="fill-charcoal">Week</text>
    </Frame>
  );
}

export function PriceChart({ inputs }: { inputs: PriceInputs }) {
  const moves = [...new Set([...PRICE_TABLE_MOVES, inputs.price])].sort((a, b) => a - b);
  const vals = moves.map((m) => priceMove(inputs.revenue, inputs.margin, m).sameVolume);
  const max = Math.max(...vals.map(Math.abs), 1);
  const mid = 150, h = 110, L = 60, W = 640;
  const bw = W / moves.length;
  return (
    <Frame title="Gross profit change by price move" desc={`Bars show the gross profit change at the same volume for price moves ${moves.join(", ")} percent, with your ${inputs.price} percent move highlighted.`}>
      <line x1={L} x2={L + W} y1={mid} y2={mid} className="stroke-charcoal/40" />
      {moves.map((m, k) => {
        const v = vals[k]!;
        const bh = (Math.abs(v) / max) * h;
        const chosen = m === inputs.price;
        const cx = L + k * bw + bw / 2;
        return (
          <g key={m}>
            <rect x={cx - bw * 0.3} width={bw * 0.6} y={v >= 0 ? mid - bh : mid} height={Math.max(bh, 1)} rx="4" className={chosen ? "fill-gold" : v < 0 ? "fill-destructive/70" : "fill-midnight"} />
            <text x={cx} y={v >= 0 ? mid - bh - 8 : mid + bh + 18} textAnchor="middle" fontSize="13" fontWeight={chosen ? 700 : 500} className="fill-foreground">{short(v)}</text>
            <text x={cx} y={290} textAnchor="middle" fontSize="13" fontWeight={chosen ? 700 : 500} className="fill-charcoal">{m > 0 ? "+" : ""}{m}%</text>
          </g>
        );
      })}
    </Frame>
  );
}

export function GridChart({ inputs }: { inputs: GridInputs }) {
  const r = gridResult(inputs);
  const maxH = Math.max(...ZONES.map((z) => r.zones[z].hours), 1);
  const cells = [
    { z: "Keep", x: 0, y: 0 },
    { z: "Grow", x: 1, y: 0 },
    { z: "Hand Over", x: 0, y: 1 },
    { z: "Let Go", x: 1, y: 1 },
  ] as const;
  const cw = 350, ch = 190;
  return (
    <Frame vb="0 0 720 400" title="Time and accountability grid" desc={ZONES.map((z) => `${z} ${r.zones[z].hours} hours`).join(", ")}>
      {cells.map((c) => {
        const zone = r.zones[c.z];
        const s = Math.sqrt(zone.hours / maxH);
        const x0 = c.x * (cw + 20);
        const y0 = c.y * (ch + 20);
        const bad = c.y === 1;
        return (
          <g key={c.z}>
            <rect x={x0} y={y0} width={cw} height={ch} rx="14" className="fill-card stroke-border" strokeWidth="1.5" />
            <rect x={x0 + 4} y={y0 + ch - 4 - (ch - 8) * s} width={(cw - 8) * s} height={(ch - 8) * s} rx="10" className={bad ? (c.z === "Let Go" ? "fill-destructive/15" : "fill-gold/25") : "fill-midnight/10"} />
            <text x={x0 + 16} y={y0 + 28} fontSize="17" fontWeight="700" className="fill-midnight">{c.z}</text>
            <text x={x0 + cw - 16} y={y0 + 28} textAnchor="end" fontSize="17" fontWeight="700" className={bad ? "fill-destructive" : "fill-midnight"}>{Math.round(zone.hours * 10) / 10} hrs</text>
            {zone.items.slice(0, 6).map((it, k) => (
              <text key={it.id} x={x0 + 16} y={y0 + 54 + k * 21} fontSize="13" className="fill-charcoal">
                {it.name.length > 40 ? `${it.name.slice(0, 38)}...` : it.name}
              </text>
            ))}
            {zone.items.length > 6 ? (
              <text x={x0 + 16} y={y0 + 54 + 6 * 21} fontSize="13" className="fill-charcoal">and {zone.items.length - 6} more</text>
            ) : null}
          </g>
        );
      })}
    </Frame>
  );
}

export function CooChart({ inputs }: { inputs: CooInputs }) {
  const r = cooResult(inputs);
  const max = Math.max(r.fullTime, r.fractional ?? 0, 1);
  const L = 200, W = 480;
  const ftW = (r.fullTime / max) * W;
  const emptyShare = Math.min(inputs.rampMonths / 12, 1);
  return (
    <Frame vb="0 0 720 230" title="Year one cost comparison" desc={`Full time all in ${aud(r.fullTime)}, with about ${r.emptyWeeks} weeks of empty seat. Fractional year one ${r.fractional === null ? "not entered" : aud(r.fractional)}.`}>
      <text x={L - 12} y={58} textAnchor="end" fontSize="14" fontWeight="600" className="fill-foreground">Full time, all in</text>
      <rect x={L} y={36} width={ftW} height={34} rx="6" className="fill-midnight" />
      <rect x={L} y={36} width={ftW * emptyShare} height={34} rx="6" className="fill-gold" />
      <text x={L + 8} y={58} fontSize="12" fontWeight="600" className="fill-midnight-deep">{r.emptyWeeks > 0 ? `Seat empty ${r.emptyWeeks} wks` : ""}</text>
      <text x={L + ftW} y={92} textAnchor="end" fontSize="13" className="fill-charcoal">{aud(r.fullTime)}</text>
      <text x={L - 12} y={148} textAnchor="end" fontSize="14" fontWeight="600" className="fill-foreground">Fractional, year one</text>
      {r.fractional === null ? (
        <text x={L} y={150} fontSize="13" className="fill-charcoal">Enter the retainer quoted to you</text>
      ) : (
        <>
          <rect x={L} y={126} width={Math.max((r.fractional / max) * W, 2)} height={34} rx="6" className="fill-steel" />
          <text x={L + Math.max((r.fractional / max) * W, 2)} y={182} textAnchor="end" fontSize="13" className="fill-charcoal">{aud(r.fractional)}</text>
        </>
      )}
      <rect x={L} y={206} width="14" height="12" className="fill-gold" />
      <text x={L + 20} y={216} fontSize="12" className="fill-charcoal">Share of year one paid before the seat is effective</text>
    </Frame>
  );
}

export function LeakChart({ inputs }: { inputs: LeakInputs }) {
  const n = inputs.answers.filter(Boolean).length;
  const seg = 72, gap = 6, L = 12;
  const colour = (k: number) => (k < 2 ? "fill-success" : k < 5 ? "fill-gold" : "fill-destructive");
  return (
    <Frame vb="0 0 720 110" title={`${n} of 9 likely leaks`} desc="Nine segments, filled from green through gold to red up to the score.">
      {Array.from({ length: 9 }, (_, k) => (
        <g key={k}>
          <rect x={L + k * (seg + gap)} y={20} width={seg} height={36} rx="6" className={k < n ? colour(k) : "fill-border"} />
          <text x={L + k * (seg + gap) + seg / 2} y={80} textAnchor="middle" fontSize="13" className="fill-charcoal">{k + 1}</text>
        </g>
      ))}
    </Frame>
  );
}

/** The 13 week scorecard grid is the visualisation for the scorecard tool. */
export function ScoreGrid({ inputs }: { inputs: ScoreInputs }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border" tabIndex={0} role="region" aria-label="13 week scorecard">
      <table className="w-full min-w-[640px] border-collapse text-xs">
        <thead>
          <tr className="bg-surface text-left text-charcoal">
            <th scope="col" className="p-2 font-semibold">Measure</th>
            <th scope="col" className="p-2 font-semibold">Owner</th>
            <th scope="col" className="p-2 font-semibold">Target</th>
            {Array.from({ length: 13 }, (_, k) => (
              <th key={k} scope="col" className="w-7 p-1 text-center font-semibold">{k + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {inputs.measures.length === 0 ? (
            <tr><td colSpan={16} className="p-3 text-charcoal">Choose at least three measures to build the grid.</td></tr>
          ) : (
            inputs.measures.map((m) => (
              <tr key={m.name} className="border-t border-border">
                <th scope="row" className="p-2 text-left font-medium text-foreground">{m.name}</th>
                <td className="p-2 text-charcoal">{m.owner || "No owner"}</td>
                <td className="p-2 text-charcoal">{m.target || "No target"}</td>
                {Array.from({ length: 13 }, (_, k) => <td key={k} className="border-l border-border p-1" />)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
